import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UseUrlStateSyncResult {
  updateUrlParams: (params: Record<string, string | null>) => void
}

/**
 * Hook for URL state synchronization
 * Provides helper to update URL params without full navigation
 */
export function useUrlStateSync(): UseUrlStateSyncResult {
  const [searchParams, setSearchParams] = useSearchParams()

  // Helper function to update URL params without full navigation
  const updateUrlParams = useCallback((params: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams)
    
    Object.entries(params).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key)
      } else {
        newParams.set(key, value)
      }
    })
    
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  return {
    updateUrlParams
  }
}

/**
 * Syncs page changes to URL
 * Only updates URL when state differs from URL to prevent circular updates
 * Only syncs AFTER initial URL has been read (hasInitialized = true)
 */
export function useSyncPageToUrl(
  currentPage: number,
  updateUrlParams: (params: Record<string, string | null>) => void,
  hasInitializedFromUrl: React.MutableRefObject<boolean>,
  workIdFromUrl?: string
) {
  const [searchParams] = useSearchParams()

  useEffect(() => {
    // Don't sync to URL until we've initialized from URL
    if (!hasInitializedFromUrl.current) return
    
    const currentUrlPage = searchParams.get('page')
    const pageStr = currentPage.toString()
    
    // Extract workId from current URL path
    const currentPath = window.location.pathname
    const pathWorkId = currentPath.match(/\/app\/work\/([^/?]+)/)?.[1]
    
    // Only update if URL path already has the right work context and page differs
    if (currentPage > 0 && currentUrlPage !== pageStr && pathWorkId === workIdFromUrl && !!workIdFromUrl) {
      updateUrlParams({ page: pageStr })
    }
  }, [currentPage, searchParams, updateUrlParams, hasInitializedFromUrl, workIdFromUrl])
}

